'use client'

import { FunctionComponent, useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import dynamic from 'next/dynamic'
import { useSiteContext, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import ErrorBoundary from '@dustin-web/microsite-components/src/utils/error-boundary'
import CookieBanner from '@dustin-web/microsite-components/src/cookie-banner'
import { DevUtils } from '@dustin-web/microsite-components/src/dev/dev-admin'
import ScrollToTopButton from '@dustin-web/microsite-baselayout/src/components/scroll-to-top-btn'
import { useAuth } from '@dustin-web/microsite-components/src/auth'
import { useCookieState } from '@dustin-web/microsite-components/src/hooks/use-cookie-state'
import { constants } from '@dustin-web/microsite-config'
import EolIe from '@dustin-web/microsite-components/src/eol-ie'
import { RenderScripts } from '@dustin-web/microsite-baselayout/src/components/3rd-party-scripts'
import ThemeColor from '@dustin-web/microsite-baselayout/src/components/theme-color'
import { useFeatureFlag } from '@dustin-web/microsite-data/src/feature-flags/client'

const MicrositeContextDevAdmin = dynamic(
  () => import('@dustin-web/microsite-components/src/dev/dev-admin')
)

type Props = {
  devUtils: DevUtils
  children: React.ReactNode
}

const AppBaseLayoutEffects: FunctionComponent<Props> = ({ devUtils, children }) => {
  const { enabled: isDevAdminEnabled } = useFeatureFlag('dev-admin-ui', true)
  const pathname = usePathname() || ''
  const { userData } = useAuth()

  const { translate } = useTranslations()
  const { cookies, setCookie } = useCookieState()
  const isAdmin = userData?.allowImpersonateContact ?? false
  const [isIE11, setIsIE11] = useState<boolean>(false)

  useEffect(() => {
    if ('ActiveXObject' in window) {
      alert(isIE11)

      setTimeout(function () {
        setIsIE11(true)
      }, 1)
    }
  }, [pathname, isIE11])

  useEffect(() => {
    if (isAdmin && !cookies.userSettings.showMargins) {
      setTimeout(() => {
        const result = confirm(translate('Show_Margins'))
        setCookie(constants.cookies.userSettings.showMargins, result)
      }, 500)
    }
  }, [isAdmin, cookies.userSettings.showMargins, translate, setCookie])

  const { siteUrl, isProxied, isDevelopment } = useSiteContext()
  const isStandaloneProxied = siteUrl.includes('dustin.services') && isProxied

  const shouldShowDevAdmin = isDevelopment || !isProxied || isStandaloneProxied || isDevAdminEnabled

  const [showDevAdmin, setShowDevAdmin] = useState(false)

  useEffect(() => {
    if (shouldShowDevAdmin) {
      setShowDevAdmin(true)
    }
  }, [shouldShowDevAdmin])

  return (
    <>
      <div className="business">
        {isIE11 === true ? (
          <div
            className="t-light-blue d-flex justify-content-center u-pb-79"
            style={{ height: '100vh', paddingTop: '80px' }}>
            <EolIe />
          </div>
        ) : (
          <>
            {children}
            <ErrorBoundary>
              <RenderScripts />
            </ErrorBoundary>
            <ErrorBoundary>
              <CookieBanner />
            </ErrorBoundary>
            <ErrorBoundary>
              <ScrollToTopButton />
            </ErrorBoundary>
            <ErrorBoundary>
              <ThemeColor />
            </ErrorBoundary>
          </>
        )}
        <ErrorBoundary>
          {showDevAdmin && <MicrositeContextDevAdmin devUtils={devUtils} />}
        </ErrorBoundary>
      </div>
    </>
  )
}

export default AppBaseLayoutEffects
